/**
 * RuleFormFields — inner form fields for create/edit gate rule dialog.
 * Separated from RuleFormDialog to keep both files under 200 lines.
 * WCAG 2.1 AA: all inputs have explicit Label (htmlFor), aria-required,
 * aria-describedby linked to error message IDs.
 */
import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CONTRACT_TYPE_OPTIONS } from '@/pages/contracts/contractTypeSchema';
import { RuleSourceField } from './RuleSourceField';
import type { RuleFormValues } from './RuleFormValues';

export type { RuleFormValues };

interface RuleFormFieldsProps {
  register: UseFormRegister<RuleFormValues>;
  errors: FieldErrors<RuleFormValues>;
  watch: UseFormWatch<RuleFormValues>;
  setValue: UseFormSetValue<RuleFormValues>;
}

const fieldCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background' +
  ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2' +
  ' disabled:cursor-not-allowed disabled:opacity-50';

const errorCls = 'mt-1 text-xs text-destructive';

export function RuleFormFields({ register, errors, watch, setValue }: RuleFormFieldsProps) {
  const enabledValue = watch('enabled');
  const selectedTypes = watch('applies_to_types') ?? [];

  const toggleType = (value: string) => {
    const next = selectedTypes.includes(value)
      ? selectedTypes.filter((t) => t !== value)
      : [...selectedTypes, value];
    setValue('applies_to_types', next, { shouldDirty: true });
  };

  return (
    <div className="space-y-4">
      {/* Rule Name */}
      <div>
        <Label htmlFor="rule_name">
          Rule Name <span aria-hidden="true" className="text-destructive">*</span>
        </Label>
        <input
          id="rule_name"
          {...register('rule_name')}
          className={fieldCls}
          aria-required="true"
          aria-describedby={errors.rule_name ? 'rule_name_err' : undefined}
          placeholder="e.g., Require GDPR compliance clause"
        />
        {errors.rule_name && (
          <p id="rule_name_err" role="alert" className={errorCls}>
            {errors.rule_name.message}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          {...register('description')}
          rows={2}
          className={`${fieldCls} resize-y`}
          placeholder="Describe what this rule checks..."
        />
      </div>

      {/* Severity + Category */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="severity">
            Severity <span aria-hidden="true" className="text-destructive">*</span>
          </Label>
          <select
            id="severity"
            {...register('severity')}
            className={fieldCls}
            aria-required="true"
            aria-describedby={errors.severity ? 'severity_err' : undefined}
          >
            <option value="blocking">Blocking</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          {errors.severity && (
            <p id="severity_err" role="alert" className={errorCls}>
              {errors.severity.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="category">Category</Label>
          <input
            id="category"
            {...register('category')}
            className={fieldCls}
            placeholder="e.g., Compliance, Signatures"
          />
        </div>
      </div>

      {/* Rule source — regulatory vs the company's own policy */}
      <RuleSourceField
        register={register}
        source={watch('source') ?? 'regulatory'}
        severity={watch('severity')}
        className={fieldCls}
      />

      {/* Applies to contract types (P2·B) */}
      <fieldset>
        <legend className="text-sm font-medium">Applies to</legend>
        <p className="mt-0.5 mb-2 text-xs text-muted-foreground">
          Leave all unchecked to apply this rule to <strong>every</strong> contract type.
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {CONTRACT_TYPE_OPTIONS.map((opt) => (
            <label key={opt.value} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input focus-visible:ring-2 focus-visible:ring-ring"
                checked={selectedTypes.includes(opt.value)}
                onChange={() => { toggleType(opt.value); }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Condition JSON */}
      <div>
        <Label htmlFor="condition_raw">
          Condition (JSON) <span aria-hidden="true" className="text-destructive">*</span>
        </Label>
        <textarea
          id="condition_raw"
          {...register('condition_raw')}
          rows={4}
          className={`${fieldCls} resize-y font-mono text-xs`}
          aria-required="true"
          aria-describedby="condition_hint condition_err"
          placeholder='{"require_compliance_pass": true}'
        />
        <p id="condition_hint" className="mt-1 text-xs text-muted-foreground">
          Enter a JSON object. Example:{' '}
          <code className="rounded bg-muted px-1 py-0.5">
            {'{'}&#34;require_signature&#34;: true{'}'}
          </code>
        </p>
        {errors.condition_raw && (
          <p id="condition_err" role="alert" className={errorCls}>
            {errors.condition_raw.message}
          </p>
        )}
      </div>

      {/* Sort Order + Enabled */}
      <div className="flex items-end gap-4">
        <div className="w-32">
          <Label htmlFor="sort_order">Sort Order</Label>
          <input
            id="sort_order"
            type="number"
            min={0}
            {...register('sort_order')}
            className={fieldCls}
            aria-describedby={errors.sort_order ? 'sort_order_err' : undefined}
          />
          {errors.sort_order && (
            <p id="sort_order_err" role="alert" className={errorCls}>
              {errors.sort_order.message}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 pb-0.5">
          <Switch
            id="enabled-toggle"
            checked={enabledValue ?? true}
            onCheckedChange={(v) => { setValue('enabled', v); }}
            aria-labelledby="enabled-label"
          />
          <Label id="enabled-label" htmlFor="enabled-toggle" className="cursor-pointer">
            Enabled
          </Label>
        </div>
      </div>
    </div>
  );
}
